<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast, wait } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Navigation from "./Navigation.svelte"
  import Button from "$app/components/ButtonSpinner.svelte"
  import { ChevronRight } from "lucide-svelte"

  import team from "@images/onboarding/team.png"

  import { onboardingStore } from "./onboarding.svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    market_segments: MarketSegmentType[]
  }

  let { market_segments }: Props = $props()

  let errors: any = $state({})
  onboardingStore.saving = false
  onboardingStore.completed = false

  let form = $state({
    affected: appStore.plan.affected,
    mobile_number: appStore.current_user.mobile_number,
    marketing_source: appStore.plan.marketing_source,
    marketing_source_detail: appStore.plan.marketing_source_detail,
    market_id: appStore.plan.market_id
  })
  if (form.affected <= 0) {
    form.affected = 1
  }

  function validForm(): boolean {
    errors = {}
    if (form.affected <= 0) {
      errors.affected = "Please enter a value of at least 1 employee"
    }
    if (!form.marketing_source || form.marketing_source.length <= 0) {
      errors.marketing_source = "Please select how you heard about us"
    }
    if (
      form.marketing_source === "Other" &&
      (!form.marketing_source_detail ||
        form.marketing_source_detail.length <= 0)
    ) {
      errors.marketing_source_detail =
        "Please provide additional details on how you heard about us."
    }
    if (!form.market_id) {
      errors.market_id = "Please select your industry"
    }

    return Object.keys(errors).length === 0
  }

  async function nextStep(): Promise<void> {
    onboardingStore.saving = true
    errors = {}

    if (!validForm()) {
      onboardingStore.completed = false
      onboardingStore.saving = false
      return
    }

    try {
      const resp = await xhr.patch(
        `/plan_onboarding/${appStore.plan.id}.json`,
        {
          user: { mobile_number: form.mobile_number },
          plan: {
            marketing_source: form.marketing_source,
            marketing_source_detail: form.marketing_source_detail,
            market_id: form.market_id,
            affected: form.affected,
            onboarding_step: onboardingStore.current_onboarding_step
          }
        }
      )

      appStore.plan.onboarding_steps = resp.data.plan.onboarding_steps
      appStore.current_user.mobile_number = resp.data.user.mobile_number
    } catch (e) {
      onboardingStore.completed = false
      if (e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
        console.log(errors)
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
      toast("There was an error saving your data", "error")
    } finally {
      onboardingStore.current_onboarding_step = onboardingStore.nextStepName
      onboardingStore.saving = false
    }
  }

  onMount(() => {
    onboardingStore.setBrowserHistory({}, "welcome")
  })
</script>

<div class="mx-auto px-2 lg:px-6 pt-4 w-full pb-8 lg:pb-20 lg:text-left">
  <div class="px-4 w-full">
    <Navigation step="welcome" on:updateStep />

    <h2 class="mt-12 text-5xl font-bold text-center text-white">
      Welcome Aboard!
    </h2>
    <div class="px-6 lg:px-12 my-12 w-full max-w-6xl mx-auto">
      <div class="grid grid-cols-2 gap-12">
        <div>
          <h2 class="text-3xl font-bold text-orange-200 text-left">
            We're glad you're here!
          </h2>
          <h3 class="mt-4 text-2xl text-white">
            Please answer the following questions to help us customize your Lead
            With Purpose experience and then click Continue to proceed.
          </h3>

          <div class="space-y-6 max-w-xl">
            <div class="mt-6 grid grid-cols-1 gap-y-4 gap-x-4">
              <div>
                <label for="plan[affected]" class="text-white text-lg">
                  Number of Employees
                </label>
                <div class="mt-2 input-cont" class:error={errors.affected}>
                  <input
                    type="number"
                    min="1"
                    bind:value={form.affected}
                    data-name="affected"
                    class="input input-lg !w-40"
                  />
                  <div class="msg">{errors.affected}</div>
                </div>
              </div>

              <div>
                <label for="plan[affected]" class="text-white text-lg">
                  Mobile Phone Number
                  <span class="italic text-sm text-gray-300">(Optional)</span>
                </label>
                <div class="mt-2 input-cont" class:error={errors.mobile_number}>
                  <input
                    type="text"
                    bind:value={form.mobile_number}
                    data-name="mobile_number"
                    class="input input-lg !w-56"
                  />
                  <div class="msg">{errors.mobile_number}</div>
                </div>
              </div>
              <div>
                <label for="plan[marketing_source]" class="text-white text-lg">
                  How did you hear about us?
                </label>
                <div
                  class="mt-2 input-cont"
                  class:error={errors.marketing_source}
                >
                  <select
                    bind:value={form.marketing_source}
                    class="input-lg !w-72"
                    data-name="marketing_source"
                    name="plan[marketing_source]"
                  >
                    <option value="Article">Article</option>
                    <option value="Support Chat">Support Chat</option>
                    <option value="Current Client Referral"
                      >Current Client Referral</option
                    >
                    <option value="Keynote">Keynote</option>
                    <option value="Podcast">Podcast</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Web Search">Web Search</option>
                    <option value="Vistage/Convene">Vistage/Convene</option>
                    <option value="Workshop">Workshop</option>
                    <option value="Other">Other</option>
                  </select>
                  <div class="msg">{errors.marketing_source}</div>
                </div>
              </div>
              {#if form.marketing_source === "Other"}
                <div>
                  <label
                    for="plan[marketing_source_detail]"
                    class="text-white text-lg"
                    >Please provide additional details on this response</label
                  >
                  <div
                    class="mt-2 input-cont"
                    class:error={errors.marketing_source_detail}
                  >
                    <input
                      type="text"
                      bind:value={form.marketing_source_detail}
                      class="input input-lg"
                    />
                    <div class="msg">{errors.marketing_source_detail}</div>
                  </div>
                </div>
              {/if}
              <div>
                <label for="plan[market_id]" class="text-white text-lg">
                  What is your industry?
                </label>
                <div class="mt-2 input-cont" class:error={errors.market_id}>
                  <select
                    class="input-lg"
                    bind:value={form.market_id}
                    data-name="market"
                  >
                    {#each market_segments as ms (ms.id)}
                      <option value={ms.id}>{ms.name}</option>
                    {/each}
                  </select>
                  <div class="msg">{errors.market_id}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <img src={team} class="shadow-lg rounded-xl" alt="Team" />
        </div>
      </div>
    </div>

    <div class="flex justify-center">
      <Button
        onclick={() => {
          nextStep()
        }}
        processing={onboardingStore.saving}
        class="btn btn-rounded success btn-big flex items-center gap-1 text-core"
      >
        CONTINUE
        <ChevronRight class="size-6" />
      </Button>
    </div>
  </div>
</div>
